import { notification } from "antd";
import { makeAutoObservable, runInAction } from "mobx";

import { api } from "../api";
import { getOnpremise } from "../api/onpremise";
import { getMyWebgis } from "../api/webgis";
import i18n from "../i18n";
import { teamStore } from "./teamStore";

import type { Country } from "../api/countries";
import type { ProfileData } from "../api/profile";
import type { SubscriptionData } from "../api/subscription";
import type { WebGisData } from "../api/webgis";

class UserStore {
  profile: ProfileData | null = null;
  subscription: SubscriptionData | null = null;
  countries: Country[] = [];
  onpremiseGuid: string | null = null;
  webgis: WebGisData | null = null;
  webgisLoaded = false;
  loading = false;
  authed: boolean | null = null;
  avatarUrl: string | null = null;
  avatarReady: boolean = false;

  constructor() {
    makeAutoObservable(this);
  }

  async load() {
    if (this.loading || this.authed !== null) return;
    this.loading = true;
    try {
      const [profile, subscription, countries, onpremiseGuid, webgis] =
        await Promise.all([
          api.profile.getProfile(),
          api.subscription.getSubscription(),
          api.countries.getCountries(i18n.language).catch(() => {
            notification.error({
              message: i18n.t("errors.shared.countries_load_error"),
            });
            return [] as Country[];
          }),
          getOnpremise(),
          getMyWebgis(),
          teamStore.loadMemberTeams(),
          teamStore.loadIncomingInvitations(),
        ]);
      runInAction(() => {
        this.profile = profile;
        this.subscription = subscription;
        this.countries = countries;
        this.onpremiseGuid = onpremiseGuid;
        this.webgis = webgis;
        this.webgisLoaded = true;
        this.authed = true;
      });
      if (profile.locale && profile.locale !== i18n.language) {
        await this.reloadCountries(profile.locale);
        i18n.changeLanguage(profile.locale);
        localStorage.setItem("lang", profile.locale);
      }
    } catch {
      localStorage.removeItem("lang");
      runInAction(() => {
        this.authed = false;
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  get initials() {
    if (!this.profile) return "";
    const { first_name, last_name, username, email } = this.profile;
    if (first_name || last_name) {
      return `${first_name?.[0] ?? ""}${last_name?.[0] ?? ""}`.toUpperCase();
    }
    return (username?.[0] ?? email?.[0] ?? "").toUpperCase();
  }

  get caption() {
    if (!this.profile) return "";
    const { first_name, last_name, username, email } = this.profile;
    const fullName = `${first_name ?? ""} ${last_name ?? ""}`.trim();
    return fullName || username || email;
  }

  get isRuRegion() {
    return this.profile?.country === "RU";
  }

  get currency() {
    const subscription = this.subscription;
    if (subscription && subscription.plan.toLowerCase() !== "lemon") {
      return subscription.currency.toUpperCase();
    }
    return this.isRuRegion ? "RUB" : "EUR";
  }

  setProfile(data: ProfileData) {
    this.profile = data;
  }

  setAvatarUrl(url: string) {
    this.avatarUrl = url;
  }

  setAvatarReady(ready: boolean) {
    this.avatarReady = ready;
  }

  setSubscription(data: SubscriptionData) {
    this.subscription = data;
  }

  setOnpremiseGuid(guid: string | null) {
    this.onpremiseGuid = guid;
  }

  setWebgis(data: WebGisData | null) {
    this.webgis = data;
    this.webgisLoaded = true;
  }

  clear() {
    this.profile = null;
    this.subscription = null;
    this.countries = [];
    this.onpremiseGuid = null;
    this.webgis = null;
    this.webgisLoaded = false;
    this.loading = false;
    this.authed = null;
    this.avatarUrl = null;
    this.avatarReady = false;
    teamStore.clear();
  }

  async reloadCountries(lang?: string) {
    try {
      const countries = await api.countries.getCountries(lang ?? i18n.language);
      runInAction(() => {
        this.countries = countries;
      });
    } catch {
      notification.error({
        message: i18n.t("errors.shared.countries_load_error"),
      });
    }
  }

  async reloadSubscription() {
    const subscription = await api.subscription.getSubscription();
    runInAction(() => {
      this.subscription = subscription;
    });
  }
}

export const userStore = new UserStore();
